import React from 'react';

import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import Spinner from '@components/Spinner';
import TIModal, { TiModalColorScheme } from '@components/TIModal';
import SubscriptionConfirmation from '@components/pages/Cancel/Confirmation/SubscriptionConfirmation';
import { CancelReasons, ConfirmationTypes } from '@components/pages/Cancel/Confirmation/constants';
import { useEmailSettings, useNewsletterSubscriptions, useShowConfirmationModal } from '@hooks';
import { dateInLocalTimezone } from '@utils/dates';

import SettingHeaderSeperator from '../SettingHeaderSeperator';
import EmailSettings from './EmailSettings';
import NotificationSettings from './NotificationSettings';

const EmailPreferences = (): JSX.Element => {
  const { newsletters, isLoadingNewsletterSubscriptions } = useNewsletterSubscriptions();
  const { userEmailSettings, isLoadingUserEmailSettings } = useEmailSettings();
  const {
    billingCycle,
    checkForConfirmationModal,
    nextBillingAmountWithTax,
    nextBillingDate,
    openConfirmationModal,
    setOpenConfirmationModal,
    subscriptionPlan,
  } = useShowConfirmationModal();
  const localNextBillingDate = dateInLocalTimezone(nextBillingDate);

  if (isLoadingNewsletterSubscriptions || isLoadingUserEmailSettings) return <Spinner />;

  return (
    <div>
      <SectionBreakTitle
        className="mb-32"
        titleClassName="text-primary-magenta"
        color={SectionColor.Magenta}
        title="Email preferences"
      />
      <div className="mb-24 font-suisse-intl text-description-lg">
        Update your subscription to our newsletters:
      </div>
      <EmailSettings
        checkForConfirmationModal={checkForConfirmationModal}
        newsletters={newsletters}
        userEmailSettings={userEmailSettings}
      />
      <SettingHeaderSeperator sectionName="Other" />
      <NotificationSettings
        checkForConfirmationModal={checkForConfirmationModal}
        userEmailSettings={userEmailSettings}
      />
      {openConfirmationModal && (
        <TIModal
          colorScheme={TiModalColorScheme.dark}
          isOpen={openConfirmationModal}
          onClose={() => setOpenConfirmationModal(false)}
        >
          <div className="p-48 text-center">
            <SubscriptionConfirmation
              billingCycle={billingCycle}
              cancelReason={CancelReasons.Mismatch}
              confirmationType={ConfirmationTypes.Set}
              nextBillingAmountWithTax={nextBillingAmountWithTax}
              nextBillingDate={localNextBillingDate}
              subscriptionPlan={subscriptionPlan}
              theme={TiModalColorScheme.dark}
            />
          </div>
        </TIModal>
      )}
    </div>
  );
};

export default EmailPreferences;
